import React, { useState, useEffect, useRef } from "react";
import { TagIcon } from "@heroicons/react/16/solid";
import { api } from "../../api";
import { Button } from "../tailwind/button";

const ContactCount = ({
  filterGroups,
  onAddTag,
  isGeneratingFilters = false,
  onCountChange,
  customerId,
}) => {
  const [count, setCount] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);

  const timeoutRef = useRef(null);
  const requestIdRef = useRef(0);

  // Debounced fetch with cancellation
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setError(null);
    const currentRequestId = ++requestIdRef.current;

    // Show "Counting contacts..." immediately
    setFetching(true);

    timeoutRef.current = setTimeout(async () => {
      try {
        const result = await api.getContactsCount({
          customerId,
          filterGroups: JSON.stringify(filterGroups || []),
        });

        if (currentRequestId !== requestIdRef.current) return;

        setCount(result.count);
        if (onCountChange) {
          onCountChange(result.count);
        }
      } catch (err) {
        if (currentRequestId !== requestIdRef.current) return;
        console.error("Contact count error:", err);
        setError(err);
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setFetching(false);
        }
      }
    }, 500);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [customerId, filterGroups, onCountChange]);

  if (fetching) {
    return (
      <div className="text-lg border-t border-gray-200 text-gray-500 py-2 h-14 flex items-center">
        Counting contacts...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-lg border-t border-gray-200 text-red-500 py-2 h-14 flex items-center">
        Error counting contacts
      </div>
    );
  }

  if (count === null) {
    return (
      <div className="text-lg border-t border-gray-200 text-red-500 py-2 h-14 flex items-center">
        Error counting contacts
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 py-2 border-t border-gray-200 h-14">
      <div className="text-lg text-gray-600 font-bold">
        <span>{count}</span> contact
        {count !== 1 ? "s" : ""}
      </div>
      {count > 0 && onAddTag && (
        <Button
          color="secondaryButton"
          onClick={() => onAddTag(filterGroups, count)}
          className="text-sm flex items-center gap-1"
          disabled={isGeneratingFilters}
        >
          <TagIcon className="h-4 w-4" />
          Add tag
        </Button>
      )}
    </div>
  );
};

export default ContactCount;
